import React, { useState, useEffect, useCallback } from "react";
import { DatePicker, Pagination } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import Title from "../../../components/Title.tsx";
import Layout from "../../../components/Layout.tsx";
import { useAppSettings } from "../../../hooks/useAppSettings.tsx";
import { readBranches } from "../../../services/branch.service.ts";
import { IBranch } from "../../../models/Branch.ts";
import AusentismoTable from "./components/AusentismoTable.tsx";
import AusentismoRow from "./components/AusentismoRow.tsx";
import AusentismoBranchRow from "./components/AusentismoBranchRow.tsx";
import SearchNomina from "../../../components/SearchNomina.tsx";
import ModalContainer from "../../../components/ModalContainer.tsx";
import {
  findNotAssistance,
  findAllSchedulesByBranch,
} from "../../../services/branchSchedule.service.ts";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const AusentismoPage = () => {
  useAppSettings();

  const [branchesData, setBranchesData] = useState<IBranch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<IBranch[]>([]);
  const [dates, setDates] = useState<any>({
    start: dayjs().startOf("month"),
    end: dayjs().endOf("month"),
  });
  const [activeTab, setActiveTab] = useState("sucursal");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [absenceData, setAbsenceData] = useState<any[]>([]);
  const [branchStats, setBranchStats] = useState<any[]>([]);
  const [debounceTimer, setDebounceTimer] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("date");
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");

  const [detailsModal, setDetailsModal] = useState<{
    open: boolean;
    title: string;
    data: any[];
  }>({
    open: false,
    title: "",
    data: [],
  });

  const fetchBranches = async () => {
    try {
      const branchList = await readBranches();
      setBranchesData(branchList);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const start = dates.start.format("YYYY-MM-DD");
      const end = dates.end.format("YYYY-MM-DD");
      const today = dayjs().format("YYYY-MM-DD");
      const now = dayjs();

      const capEnd = dayjs(dates.end).isAfter(now) ? today : end;

      const offset = (currentPage - 1) * pageSize;
      const queryParams = [
        `dateStart=${start}`,
        `dateEnd=${end}`,
        `limit=${pageSize}`,
        `offset=${offset}`,
        `sortBy=${sortBy}`,
        `order=${order}`,
        searchTerm ? `search=${searchTerm}` : "",
      ]
        .filter(Boolean)
        .join("&");

      const response = await findNotAssistance(queryParams);
      setTotalRecords(response.total || 0);
      const scheduleRecords = response.records || [];

      const missedSchedules = scheduleRecords.filter((r: any) => {
        if (r.assistanceCheck || r.assistanceCheckout) return false;
        if (r.isReplacement || r.replacement || r.completed) return false;

        const recordDateStr = dayjs(r.exactDate).format("YYYY-MM-DD");
        const isPast = dayjs(recordDateStr).isBefore(dayjs(today));
        const isToday = recordDateStr === today;

        if (isPast) return true;
        if (isToday) {
          const startTimeStr = r.startTime?.split(" - ")?.[0];
          if (!startTimeStr) return false;
          const [h, m] = startTimeStr.split(":").map(Number);
          const scheduleTime = dayjs()
            .set("hour", h)
            .set("minute", m)
            .set("second", 0);
          return now.diff(scheduleTime, "minute") > 15;
        }
        return false;
      });

      const finalIncidences = missedSchedules.map((r: any) => ({
        id: r.partner?.id,
        name: `${r.partner?.person?.firstName || ""} ${r.partner?.person?.lastName || ""}`,
        branch: r.branch?.name || "N/A",
        branchId: r.branch?.id,
        falta: dayjs(r.exactDate).format("DD/MM/YYYY"),
        exactDate: r.exactDate,
        tipo: r.assistanceStatus || "Falta Injustificada",
        position: r.positionName || r.position?.name || "Sin posición",
      }));
      setAbsenceData(finalIncidences);

      const branchesToProcess =
        selectedBranches.length > 0 ? selectedBranches : branchesData;

      const statsQuery = `dateStart=${start}&dateEnd=${end}&limit=5000&sortBy=date&order=DESC`;
      const statsResponse = await findNotAssistance(statsQuery);
      const allMissedInPeriod = (statsResponse.records || [])
        .filter((r: any) => {
          if (r.assistanceCheck || r.assistanceCheckout) return false;
          if (r.isReplacement || r.replacement || r.completed) return false;
          const recordDateStr = dayjs(r.exactDate).format("YYYY-MM-DD");
          if (dayjs(recordDateStr).isBefore(dayjs(today))) return true;
          if (recordDateStr === today) {
            const startTimeStr = r.startTime?.split(" - ")?.[0];
            if (!startTimeStr) return false;
            const [h, m] = startTimeStr.split(":").map(Number);
            const scheduleTime = dayjs()
              .set("hour", h)
              .set("minute", m)
              .set("second", 0);
            return now.diff(scheduleTime, "minute") > 15;
          }
          return false;
        })
        .map((r: any) => ({
          id: r.partner?.id,
          branchId: r.branch?.id,
          falta: dayjs(r.exactDate).format("DD/MM/YYYY"),
          position: r.positionName || r.position?.name || "Sin posición",
        }));

      const stats = await Promise.all(
        branchesToProcess.map(async (branch) => {
          const branchIdNum = Number(branch.id);
          const branchIncidencesInPeriod = allMissedInPeriod.filter(
            (r: any) => Number(r.branchId) === branchIdNum,
          );

          const dailyMap = new Map();
          branchIncidencesInPeriod.forEach((r: any) =>
            dailyMap.set(`${r.id}-${r.falta}`, r),
          );
          const totalFaltas = dailyMap.size;

          const positionSummaryMap = new Map();
          Array.from(dailyMap.values()).forEach((r: any) => {
            const current = positionSummaryMap.get(r.position) || 0;
            positionSummaryMap.set(r.position, current + 1);
          });

          const positionSummary = Array.from(positionSummaryMap.entries()).map(
            ([position, count]) => ({ position, count }),
          );

          const totalQuery = `dateStart=${start}&dateEnd=${capEnd}&limit=1`;
          const totalResponse = await findAllSchedulesByBranch(
            branchIdNum,
            totalQuery,
          );
          const totalTurnos = totalResponse.total || 0;

          return {
            id: branchIdNum,
            branch: branch.name,
            totalFaltas,
            percentage: totalTurnos > 0 ? (totalFaltas / totalTurnos) * 100 : 0,
            details: positionSummary.sort((a, b) => b.count - a.count),
          };
        }),
      );

      let filteredStats = stats;
      if (searchTerm && activeTab === "sucursal") {
        const lowerSearch = searchTerm.toLowerCase();
        filteredStats = stats.filter((s) =>
          s.branch.toLowerCase().includes(lowerSearch),
        );
      }
      setBranchStats(filteredStats);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    dates,
    selectedBranches,
    searchTerm,
    branchesData,
    activeTab,
    currentPage,
    sortBy,
    order,
    pageSize,
  ]);

  useEffect(() => {
    if (branchesData.length > 0) {
      fetchData();
    }
  }, [fetchData, branchesData]);

  const handleDateChange = (newDates: any) => {
    if (newDates && newDates.length === 2 && newDates[0] && newDates[1]) {
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setDates({ start: newDates[0], end: newDates[1] });
        setCurrentPage(1);
      }, 500);
      setDebounceTimer(timer);
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setOrder(order === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(key);
      setOrder("ASC");
    }
    setCurrentPage(1);
  };

  const toggleBranch = (branch: IBranch) => {
    setSelectedBranches((prev) => {
      const isSelected = prev.some((b) => b.id === branch.id);
      setCurrentPage(1);
      return isSelected
        ? prev.filter((b) => b.id !== branch.id)
        : [...prev, branch];
    });
  };

  const showBranchDetails = (branchName: string, items: any[]) => {
    setDetailsModal({
      open: true,
      title: `Resumen de faltas por puesto - ${branchName}`,
      data: items,
    });
  };

  const formatDateWithDay = (dateStr: string) => {
    const date = dayjs(dateStr, "DD/MM/YYYY");
    const formatted = date.locale("es").format("dddd, DD [de] MMMM [de] YYYY");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  return (
    <Layout>
      <Title
        baseTitle="Táctica Operativa"
        basePath="/tactica-operativa"
        activeTitle="Ausentismo"
        title="Reporte de Ausentismo"
      />

      <div
        className="card border-0 m-4 rounded-4 shadow-sm"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div className="p-4">
          <div className="d-flex justify-content-center align-items-center mb-4">
            <div style={{ width: "100%", maxWidth: "500px" }}>
              <RangePicker
                format="DD [de] MMMM [de] YYYY"
                placeholder={["Fecha de inicio", "Fecha de fin"]}
                size="large"
                allowClear={false}
                value={[dates.start, dates.end]}
                onChange={handleDateChange}
                locale={locale}
                style={{
                  width: "100%",
                  borderRadius: "16px",
                  border: "1px solid #e5e7eb",
                  padding: "0.75rem 1rem",
                  fontSize: "0.95rem",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
                className="custom-date-picker"
              />
            </div>
          </div>

          <div
            className="d-flex justify-content-center align-items-center flex-wrap"
            style={{ gap: "0.75rem" }}
          >
            {branchesData.map((branch) => {
              const isSelected = selectedBranches.some(
                (b) => b.id === branch.id,
              );
              return (
                <button
                  key={branch.id}
                  onClick={() => toggleBranch(branch)}
                  className="border-0 text-sm font-medium transition-all duration-300 shadow-sm"
                  style={{
                    background: isSelected
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "#f3f4f6",
                    color: isSelected ? "#ffffff" : "#4b5563",
                    borderRadius: "12px",
                    padding: "0.6rem 1.2rem",
                    minWidth: "120px",
                    transform: isSelected ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  {branch.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="card border-0 m-4 rounded-4 shadow-sm"
        style={{ overflow: "hidden" }}
      >
        <div style={{ backgroundColor: "#ffffff" }}>
          <div className="d-flex justify-content-between align-items-center px-4 pt-3 pb-2">
            <div className="d-flex gap-2">
              {["sucursal", "chilitos"].map((tab) => (
                <button
                  key={tab}
                  className={`btn px-4 py-2 fw-bold d-flex align-items-center ${
                    activeTab === tab
                      ? "text-primary bg-primary bg-opacity-10"
                      : "text-muted"
                  }`}
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                  style={{
                    borderRadius: "12px",
                    backgroundColor:
                      activeTab === tab ? "#eff6ff" : "transparent",
                  }}
                >
                  <i
                    className={`fa-solid fa-${tab === "sucursal" ? "store" : "users"} me-2`}
                  ></i>
                  Por {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            <div style={{ width: "300px" }}>
              <SearchNomina
                placeholder={
                  activeTab === "sucursal"
                    ? "Buscar sucursal..."
                    : "Buscar colaborador..."
                }
                onChangeDebounced={(val) => {
                  setSearchTerm(val.trim());
                  setCurrentPage(1);
                }}
                onClearImmediate={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                delayMs={300}
                className="shadow-none border-1"
              />
            </div>
          </div>

          <div className="px-4 pb-4">
            {loading ? (
              <div className="d-flex justify-content-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
              </div>
            ) : (
              <>
                {activeTab === "sucursal" && (
                  <AusentismoTable
                    columns={[
                      { label: "SUCURSAL", key: "branch", sortable: false },
                      { label: "NO* FALTAS", key: "faltas", sortable: false },
                      {
                        label: "% DE AUSENTISMO",
                        key: "percentage",
                        sortable: false,
                      },
                    ]}
                  >
                    {branchStats.map((row) => (
                      <AusentismoBranchRow
                        key={row.id}
                        record={row}
                        onShowDetails={showBranchDetails}
                      />
                    ))}
                  </AusentismoTable>
                )}

                {activeTab === "chilitos" && (
                  <>
                    <AusentismoTable
                      columns={[
                        { label: "COLABORADOR", key: "name", sortable: true },
                        { label: "SUCURSAL", key: "branch", sortable: true },
                        { label: "PUESTO", key: "position", sortable: true },
                        { label: "FECHA FALTA", key: "date", sortable: true },
                      ]}
                      onSort={handleSort}
                      sortBy={sortBy}
                      order={order}
                    >
                      {absenceData.map((row) => (
                        <AusentismoRow
                          key={`${row.id}-${row.falta}`}
                          record={{
                            ...row,
                            falta: formatDateWithDay(row.falta),
                          }}
                        />
                      ))}
                    </AusentismoTable>
                    <div className="d-flex justify-content-end mt-3">
                      <Pagination
                        current={currentPage}
                        total={totalRecords}
                        pageSize={pageSize}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                        hideOnSinglePage={totalRecords <= pageSize}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ModalContainer
        open={detailsModal.open}
        title={detailsModal.title}
        closeFn={() => setDetailsModal({ ...detailsModal, open: false })}
        size="lg"
      >
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr style={{ backgroundColor: "#f9fafb" }}>
                <th
                  className="px-4 py-2 border-0 text-muted"
                  style={{ fontSize: "0.8rem", fontWeight: 700 }}
                >
                  PUESTO
                </th>
                <th
                  className="px-4 py-2 border-0 text-muted text-center"
                  style={{ fontSize: "0.8rem", fontWeight: 700 }}
                >
                  NO* FALTAS
                </th>
              </tr>
            </thead>
            <tbody>
              {detailsModal.data.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td className="px-4 py-2 align-middle">
                    <span style={{ fontWeight: 600, color: "#374151" }}>
                      {item.position}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-middle text-center">
                    <span
                      className="px-3 py-1 rounded-pill"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        backgroundColor: "#fef2f2",
                        color: "#dc2626",
                        border: "1px solid #fecaca",
                      }}
                    >
                      {item.count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ModalContainer>
    </Layout>
  );
};

export default AusentismoPage;
